import {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type ElementType,
  type JSX,
} from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootButton, { type ButtonTypeMap as MuiButtonTypeMap } from '@mui/material/Button';
import { type BaseProps, type OverridableTypeMap } from '@mui/material/OverridableComponent';

import { type TitleCase } from '@craft/lib/formatters';

import { interactableStyle } from '../../util/interaction/parsing';
import {
  type InteractablePointerEventProps,
  type InteractableSxProps,
} from '../../util/interaction/props';
import { Interaction, InteractionIndicator } from '../../util/interaction/types';
import { mergeSx } from '../../util/mui/sx';
import { Icon } from '../icons/Icon';
import { type IconProp, type IconProps } from '../icons/types';
import { inheritedTypographySx } from '../typography/types';
import { Typography } from '../typography/Typography';

import * as constants from './constants';
import { getButtonSizeSx, parseButtonIconGap, parseButtonIconSize } from './style';
import {
  ButtonDefaultSize,
  ButtonDiscreteSize,
  type ButtonFontWeight,
  type ButtonSize,
  type ButtonVariant,
  getMuiButtonVariant,
  isLinkOrInlineLinkVariant,
  isMuiButtonSize,
  parseButtonFontWeight,
} from './types';

type DefaultOmitableButtonProp =
  | 'disabled'
  | 'disableFocusRipple'
  | 'disableRipple'
  | 'disableTouchRipple'
  | 'endIcon'
  | 'loading'
  | 'size'
  | 'startIcon'
  | 'variant';

type OmitableButtonProp<E extends ElementType, T extends OverridableTypeMap> =
  | Exclude<keyof BaseProps<T>, DefaultOmitableButtonProp>
  | keyof ComponentPropsWithoutRef<E>;

type ButtonTypeMap<E extends ElementType> = MuiButtonTypeMap<{ component?: E }, E>;

export type ButtonDefaultComponent = MuiButtonTypeMap['defaultComponent'];

/**
 * Represents the root MUI props for the {@link Button} component.
 *
 * This type must be generated by distributing over the union {@link T}, which is a type map that
 * already distributes over the union {@link E}.
 *
 * This type is analogous to Material UI's {@link OverrideProps} type, with the one exception being
 * the omission of the types from the {@link BaseProps<T>} shown below.  This omission has to be
 * performed on the {@link BaseProps<T>}, instead of on the overall resulting type
 * {@link RootButtonProps}, due to Material UI internal type definitions related to the types
 * used below.
 *
 * @see https://mui.com/material-ui/guides/composition/#generic for more information related to the
 * extension of generically typed Material UI components.
 */
type RootButtonProps<
  E extends ElementType,
  T extends OverridableTypeMap,
  O extends OmitableButtonProp<E, T> = never,
> = T extends OverridableTypeMap
  ? Omit<BaseProps<T>, DefaultOmitableButtonProp | O> &
      Omit<ComponentPropsWithoutRef<E>, keyof BaseProps<T> | O>
  : never;

/**
 * Represents the element state SX props that are inherited from {@link InjectableSxProps}
 * for use in the {@link Button} component.
 *
 * Eventually this will be expanded such that the {@link Button} component supports all props in
 * {@link ButtonSxProps} for purposes of customizing focus/hover indicator, but that requires
 * additional work to ensure that the {@link Button} favors outlines instead of borders even when
 * MUI's own button variants are used.
 */
type InheritedStateSxProps = InteractablePointerEventProps &
  Pick<InteractableSxProps, 'focusIndicator' | 'hoverIndicator'>;

export type ButtonPartialProps = {
  readonly endIcon?: IconProp;
  readonly endIconProps?: IconProps;
  readonly fontWeight?: ButtonFontWeight;
  /**
   * The size of the {@link Button}, which affects its dimensions.  This prop is only applicable
   * when the 'variant' is not "link".
   */
  readonly size?: ButtonSize;
  readonly startIcon?: IconProp;
  readonly startIconProps?: IconProps;
  readonly target?: string;
  readonly textTransform?: 'capitalize' | 'lowercase' | 'none' | 'uppercase';
  readonly type?: 'button' | 'reset' | 'submit';
  readonly underline?: boolean;
  /**
   * Defines the variant of the {@link Button}, which affects its appearance.
   *
   * @see {@link ButtonVariant}
   */
  readonly variant?: ButtonVariant;
} & InheritedStateSxProps;

/**
 * Represents the props for the {@link Button} component, which are a combination of internally
 * defined props and props that are extended directly from MUI, {@link RootButtonProps}.
 */
export type ButtonProps<
  E extends ElementType = ButtonDefaultComponent,
  P = ButtonPartialProps,
  O extends OmitableButtonProp<E, ButtonTypeMap<E>> = never,
> = P & RootButtonProps<E, ButtonTypeMap<E>, O>;

type ButtonComponentType = <E extends ElementType = ButtonDefaultComponent>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & ButtonProps<E>,
) => JSX.Element;

type ButtonPartialComponentType = <E extends ElementType = ButtonDefaultComponent>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & ButtonProps<
    E,
    Omit<ButtonPartialProps, 'size'>
  >,
) => JSX.Element;

export const ExtensibleButton: ButtonComponentType = <
  E extends ElementType = ButtonDefaultComponent,
>({
  children,
  endIcon,
  endIconProps,
  focusIndicator = InteractionIndicator.Border,
  fontWeight,
  hoverIndicator = InteractionIndicator.Background,
  isDisabled,
  isLoading,
  isLocked,
  ref,
  size: _size,
  startIcon,
  startIconProps,
  textTransform: _textTransform,
  underline,
  variant = constants.DefaultButtonVariant,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & ButtonProps<E>) => {
  const defaultSize = isLinkOrInlineLinkVariant(variant) ? 'auto' : ButtonDefaultSize;
  const size = _size ?? defaultSize;

  const defaultTextTransform = variant === 'inline-link' ? 'none' : undefined;
  const textTransform = _textTransform ?? defaultTextTransform;

  return (
    <RootButton
      {...props}
      disabled={isDisabled}
      disableFocusRipple
      disableRipple
      disableTouchRipple
      endIcon={
        endIcon ? (
          <Icon size={parseButtonIconSize(size)} {...endIconProps} icon={endIcon} />
        ) : undefined
      }
      loading={isLoading}
      ref={ref}
      size={isMuiButtonSize(size) ? size : ButtonDefaultSize}
      startIcon={
        startIcon ? (
          <Icon size={parseButtonIconSize(size)} {...startIconProps} icon={startIcon} />
        ) : undefined
      }
      sx={mergeSx(
        { textDecoration: underline ? 'underline' : undefined },
        t =>
          interactableStyle(
            t,
            [
              {
                indicator: focusIndicator ?? undefined,
                interactions: [Interaction.FocusVisible, Interaction.Focus],
                isDisabled: isLinkOrInlineLinkVariant(variant),
              },
              {
                interactions: [Interaction.FocusVisible, Interaction.Focus],
                isDisabled: !isLinkOrInlineLinkVariant(variant),
                style: {
                  color: t.palette.primary.dark,
                },
              },
              {
                interaction: Interaction.Hover,
                isEnabled: isLinkOrInlineLinkVariant(variant),
                style: {
                  backgroundColor: 'transparent',
                  color: t.palette.primary.dark,
                  textDecoration: underline ? 'underline' : undefined,
                },
              },
              {
                indicator: hoverIndicator ?? undefined,
                interaction: Interaction.Hover,
                isDisabled: isLinkOrInlineLinkVariant(variant),
                overrides: { backgroundColor: variant === 'outlined' ? 'default' : null },
                style: {
                  textDecoration: underline ? 'underline' : undefined,
                },
              },
            ],
            {
              selectors: {
                [Interaction.FocusVisible]: ['&:focus-visible', '&.Mui-focusVisible'],
              },
            },
          ),
        {
          flexGrow: 0,
          flexShrink: 0,
          fontWeight: parseButtonFontWeight(fontWeight, variant),
          textTransform,
          whiteSpace: 'nowrap',
          ...getButtonSizeSx(size, variant),
          '& > .MuiButton-endIcon': {
            flexShrink: 0,
            marginLeft: parseButtonIconGap(size),
            marginRight: 0,
          },
          '& > .MuiButton-loadingIndicator': {
            color: 'inherit',
          },
          '& > .MuiButton-startIcon': {
            flexShrink: 0,
            marginLeft: 0,
            marginRight: parseButtonIconGap(size),
          },
        },
        variant === 'inline-link'
          ? { ...inheritedTypographySx(['fontSize', 'letterSpacing']), verticalAlign: 'baseline' }
          : undefined,
        props.sx,
        { pointerEvents: isLocked || isLoading || isDisabled ? 'none' : 'auto' },
      )}
      variant={getMuiButtonVariant(variant)}
    >
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography
          {...{ [constants.InnerButtonTextDataAttrKey]: constants.InnerButtonTextDataAttrType }}
          data-attr-content={children}
          inherit
          lineClamp={1}
          truncate
          wordBreak='break-all'
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </RootButton>
  );
};

const LocalButton = ExtensibleButton as ButtonComponentType &
  Partial<Record<TitleCase<ButtonDiscreteSize>, ButtonPartialComponentType>>;

const createButtonPartial = (size: ButtonDiscreteSize): ButtonPartialComponentType => {
  const ButtonPartial: ButtonPartialComponentType = <
    E extends ElementType = ButtonDefaultComponent,
  >({
    ref,
    ...props
  }: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & ButtonProps<
    E,
    Omit<ButtonPartialProps, 'size'>
  >) => (
    <LocalButton
      {...(props as ButtonProps<E, Omit<ButtonPartialProps, 'size'>>)}
      ref={ref}
      size={size}
    />
  );
  return ButtonPartial;
};

LocalButton.Xsmall = createButtonPartial(ButtonDiscreteSize.XSmall);
LocalButton.Small = createButtonPartial(ButtonDiscreteSize.Small);
LocalButton.Medium = createButtonPartial(ButtonDiscreteSize.Medium);
LocalButton.Large = createButtonPartial(ButtonDiscreteSize.Large);

export const Button = LocalButton as ButtonComponentType &
  Record<TitleCase<ButtonDiscreteSize>, ButtonPartialComponentType>;
